import React, { useState, useEffect } from 'react';
import { Send, RefreshCw } from 'lucide-react';
import { ApiService, Transaction } from '../../services/api';
import { usePagination } from '../../hooks/usePagination';
import toast from 'react-hot-toast';
import SwapModal from '../../components/modals/admin/SwapModal';
import AdminTransactionsTable from '../../components/tables/admin/AdminTransactionsTable';
import TransactionDetailsModal from '../../components/modals/TransactionDetailsModal';

const AdminSwap: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [isExecutingSwap, setIsExecutingSwap] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Date filtering state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Pagination
  const transactionsPagination = usePagination(transactions, { itemsPerPage: 10 });

  useEffect(() => {
    fetchSwapTransactions();
  }, [sortBy, sortOrder, startDate, endDate]);

  const fetchSwapTransactions = async () => {
    if (transactions.length === 0) {
      setLoading(true);
    }
    
    try {
      const data = await ApiService.getSwapTransactions(
        sortBy || undefined,
        sortOrder,
        startDate && startDate.trim() !== '' ? startDate : undefined,
        endDate && endDate.trim() !== '' ? endDate : undefined
      );
      setTransactions(data);
    } catch (error) {
      console.error('Failed to fetch swap transactions:', error);
      toast.error('Failed to load swap transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string, order: 'asc' | 'desc') => {
    setSortBy(field);
    setSortOrder(order);
  };

  const handleStartDateChange = (date: string | undefined) => {
    setStartDate(date || '');
  };

  const handleEndDateChange = (date: string | undefined) => {
    setEndDate(date || '');
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleViewTransactionDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleViewOnSolscan = (transaction: Transaction) => {
    const network = import.meta.env.VITE_SOLANA_NETWORK || 'devnet';
    const url = `https://solscan.io/tx/${transaction.solanaSignature}${network === 'devnet' ? '?cluster=devnet' : ''}`;
    window.open(url, '_blank');
  };

  const handleRefresh = () => {
    fetchSwapTransactions();
    toast.success('Swap transactions refreshed');
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2777EF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Swap</h1>
          <p className="text-sm sm:text-base text-gray-400">Execute and manage direct swaps between companies</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3 sm:space-y-0 w-full sm:w-auto">
          <button
            onClick={handleRefresh}
            className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-gray-700 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#1F2937] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2777EF]"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowSwapModal(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2777EF] hover:bg-[#1F5FD3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2777EF]"
          >
            <Send className="mr-2 h-4 w-4" />
            Execute Swap
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-[#182233] shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-2">About Swaps</h3>
        <p className="text-sm text-gray-400">
          Swaps enable direct money transfers between companies with dynamic exchange rates. 
          Each swap requires a quote that includes the exchange rate and maximum transaction amount. 
          Quotes expire after 1 minute and must be refreshed for new swaps.
        </p>
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="bg-[#0D1421] p-4 rounded-lg">
            <p className="text-sm text-gray-400">Total Swaps</p>
            <p className="text-2xl font-bold text-white mt-1">{transactions.length}</p>
          </div>
          <div className="bg-[#0D1421] p-4 rounded-lg">
            <p className="text-sm text-gray-400">Quote Duration</p>
            <p className="text-2xl font-bold text-white mt-1">60s</p>
          </div>
          <div className="bg-[#0D1421] p-4 rounded-lg">
            <p className="text-sm text-gray-400">Exchange Rate Range</p>
            <p className="text-2xl font-bold text-white mt-1">92-99%</p>
          </div>
        </div>
      </div>

      {/* Swap Transactions Table */}
      <div className="bg-[#182233] shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-white mb-4">Swap Transactions</h3>
          <AdminTransactionsTable
            transactions={transactionsPagination.paginatedData}
            onViewDetails={handleViewTransactionDetails}
            onViewOnSolscan={handleViewOnSolscan}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            onClearDateFilter={handleClearDateFilter}
            showDateFilter={true}
            currentPage={transactionsPagination.currentPage}
            totalPages={transactionsPagination.totalPages}
            totalItems={transactionsPagination.totalItems}
            itemsPerPage={transactionsPagination.itemsPerPage}
            onPageChange={transactionsPagination.setCurrentPage}
            onItemsPerPageChange={transactionsPagination.setItemsPerPage}
            showPagination={true}
          />
        </div>
      </div>

      {/* Modals */}
      <SwapModal
        isOpen={showSwapModal}
        onClose={() => setShowSwapModal(false)}
        onSuccess={fetchSwapTransactions}
        isExecuting={isExecutingSwap}
        setIsExecuting={setIsExecutingSwap}
      />

      <TransactionDetailsModal
        isOpen={showTransactionDetails}
        onClose={() => setShowTransactionDetails(false)}
        transaction={selectedTransaction}
        onViewOnSolscan={handleViewOnSolscan}
      />
    </div>
  );
};

export default AdminSwap;


